
# why do dis?

Whoops, I swore. Well, supposedly the more we curse the more honest we are according to [Frankly, We Do Give a Damn: The Relationship Between Profanity and Honesty | Stanford Graduate School of Business](https://www.gsb.stanford.edu/faculty-research/publications/frankly-we-do-give-damn-relationship-between-profanity-honesty).

I want to put this guide together for someone who uses all 5 major platforms in 2019 for making music (in priority order for me):

- Windows
- macOS
- iOS
- Linux
- Android

# hardware

- [Amazon.com: Alesis Recital | 88-Key Beginner Digital Piano with Full-Size Semi-Weighted Keys, Power Supply, Built-In Speakers and 5 Premium Voices (Amazon Exclusive): Musical Instruments](https://www.amazon.com/Alesis-Recital-Full-Size-Semi-Weighted-Built/dp/B01DZXE9NC)
- [Amazon.com: Mackie PROFX8V2 8-Channel Compact Mixer with USB and Effects: Musical Instruments](https://www.amazon.com/Mackie-PROFX8V2-8-Channel-Compact-Effects/dp/B00VUU77XW/ref=sr_1_3?keywords=mackie+mixer&qid=1557183670&s=musical-instruments&sr=1-3)
- [Amazon.com: Neewer NW-700 Professional Condenser Microphone & NW-35 Suspension Boom Scissor Arm Stand with XLR Cable and Mounting Clamp & NW-3 Pop Filter & 48V Phantom Power Supply with Adapter Kit: Musical Instruments](https://www.amazon.com/Neewer-Professional-Condenser-Microphone-Suspension/dp/B01D4KYRYC/ref=sr_1_2?keywords=Neewer+NW-700&qid=1557183741&s=musical-instruments&sr=1-2)

# hardware setup

Properly using a Mackie mixer without any digital audio production experience whatsoever took me a couple days to get perfectly right. You'll be using your mixer/audio interface as both your audio input and audio output on your machine. First thing's first, go buy a bunch of 1/4" to 1/8" aux adapters to making life hella easier on urself: [Amazon.com: VCE 2-Pack 6.35mm (1/4 inch) to 3.5mm(1/8 inch) Male to Female Stereo Jack Adapter: Electronics](https://www.amazon.com/gp/product/B01EJ5UV3C/ref=ppx_yo_dt_b_search_asin_title?ie=UTF8&psc=1). Proceed to plug in any headphones to the far right of your Mackie mixer in _"PHONES"_. Next plugin your XLR cable from your microphone into channel 1 at the top where it says _"MIC"_. Be sure to turn up the level all the way down at the bottom on the slider. In order for you mic to work and be heard you need to power on your mixer and be sure to have the _"48V PHANTOM POWER"_ button on and displaying red. You should now be able to hear yourself through the microphone into your headphones.

Next you're going to want to plug in USB from your mixer into your computer and set the default input and output device to the mixer. What's super fun now is that you can adjust the _"INPUT LEVEL"_ on the far right side of your mixer and directly below it you can adjust the volume in your ears through the _"PHONES"_. The extra extra fun part here is that the output from your computer can be mixed with your voice and flipped back around in the output by pressing the _"USB THRU"_ button.

Next up is your Alesis 88 key piano to plug in through USB to your computer and you'll also want to grab another 1/4" to 1/8" and snag one of these: [Amazon.com: Hosa YMP-434 3.5 mm TRSF to Dual 1/4" TS Stereo Breakout Cable: Musical Instruments](https://www.amazon.com/gp/product/B0010D0HO0/ref=ppx_yo_dt_b_asin_title_o09_s00?ie=UTF8&psc=1) to plug in the headphone output of your keyboard into your mixer. This means that nobody has to hear your atrocious noises at the wee hours of the night and you can hear yourself playing while listening to whatever shit tutorial you picked. I personally have been enjoying listening to Jacob Collier's live streams while I try and find the key he's in.

# software setup

Programs:

- Ardour (currently on version 5+)
  - be a good sport and grab the the subscription version and contribute at least $1 a month to their development. Ardour is by far the only well maintained open source/cross platform DAWs that just plain works
- CMDer bash emulator
- WSL (Windows subsystem for Linux) by installing "Ubuntu" app on Windows app store

Plugins:

- lists I've found
  - [Moonlight Sonata 3rd - Ardour - Free VST Plugins - YouTube](https://www.youtube.com/watch?v=RLAKewO6Zx8)
  - [The best FREE music tools in 2019! - YouTube](https://www.youtube.com/watch?v=L9sRTxDxIHE)
- 1 by 1
  - Piano One: [The Best Computer Music and Acoustic Technology](http://www.supremepiano.com/index.html?piano1.html)

# artists to look at

My pre-req for which artists I pay attention to is super opinionated but I like a good mix of music theory (think circle of fifths) and MIDI magic. Jacob Collier by far is who I strive to be as far as computer skills and mastery of the arrangement. 

- [ANDREW HUANG - YouTube](https://www.youtube.com/channel/UCdcemy56JtVTrsFIOoqvV8g)
- deadmau5
- Jacob Collier
  - [How Jacob Collier Uses Microtonality, Pitch and Temperament - YouTube](https://www.youtube.com/watch?v=NHC2XNGerW4)
- Charlie Puth

# instrument keys

[Concert Pitch Transposition](http://bandnotes.info/tidbits/scales/transposition.htm)

_Balanced_ instruments that can _center_ themselves around a "concert C" such as guitar and piano are made this way to have a certain balance. Since I have an 88 key piano usually C is pretty close to the middle. Whereas certain instruments stay between a couple "octaves" like a saxophone where you want to center its music notation around a certain spot in written music so transposition will have to happen in some cases.

B flat instruments:
- trumpet
- trombone
- soprano sax
- tenor sax
- bass clarinet

E flat instruments:
- alto sax
- baritone sax

# how to find the key of a song

[Get Audio Analysis for a Track | Spotify for Developers](https://developer.spotify.com/documentation/web-api/reference/tracks/get-audio-analysis/)

# my ongoing music knowledge

Start simple, you don't need to know how to read music but it might be a beautiful place to start. You hopefully have some sort of keyboard in front of you and if you followed my setup then you can make a racket at all hours and play around now. Start with chords and the [Circle of fifths](https://en.wikipedia.org/wiki/Circle_of_fifths).

Each letter on the top is a "major" chord meaning the letter is the root followed by a perfect third and a perfect fifth.

Let's start with C major triad which is C + E + G, followed by D major triad (D + F# + A), etc. The further you go around on the right side of the circle the "brighter" it's supposed to feel. Counter-clockwise should resemble it getting "darker". A fun little video to follow along with is [Jacob Collier Masterclass in Norway May 2017 - YouTube](https://www.youtube.com/watch?v=aKDM__VFaxg&t=1s).

Jacob explains how you can start at any "note/chord" and chromatically move up and down a half step in any direction. The tonic/root exists as home base and you go on a journey to eventually get back to where you started. Other fun things to note from this video is G half sharp and other fancy subtones/microtones that exists in perfect harmonic series music instead of the organized piano. Fun part is on your VST instruments you can usually bend the pitch to exactly what you want by just subtracting a few hertz. You should note that there is not such thing as a G half sharp on the piano in any way.

# example songs

[here comes the sun by jacob collier](https://open.spotify.com/track/2KQlkzT8Gg1lFDU0y5TYeK?si=NPWYXtp2TASkyQdGadiE_w)
- A major triad = A + C# + E
- B major triad = B + D# + F#

[Ed Sheeran - Perfect (Official Music Video) - YouTube](https://www.youtube.com/watch?v=2Vv-BfVoq4g)

# memorization list

solfege = do re mi fa so la ti do
solfege chromatic = do di re ri mi fa fi so si la li ti do ti te la le so se fa mi me re ra do
                    0  1  2  3  4  5  6  7  8  9  10 11 12 13 14 15 16 17 18 19 20 21 22 23 24

sharps  = Fat Cunts Get Drunk At Every Barbecue

all     = BEAD GCF 

- major chords
  - C  = C  + E  + G  + B
  - G  = G  + B  + D  + F#
  - D  = D  + F# + A  + C#
  - A  = A  + C# + E  + G#
  - E  = E  + C# + B  + D#
  - B  = B  + D# + F# + A#
  - F# = 
  - C# =
  - Ab =
  - Eb =
  - Bb =
  - F  =
- minor chords
  - a  = A  + C  + E  + G
  - e  = E  + G  + B  + D
  - b  = B  + D  + F# + A
  - f# = F# + A  + C# + E
  - c# = C# + E  + G# + B
  - g# = G# + B  + D# + F#
  - eb = Eb + Gb + Bb + Db
  - bb = Bb + Db + F  + Ab
  - f  = F  + Ab + C  + Eb
  - c  = C  + Eb + G  + Bb
  - g  = G  + Bb + D  + F
  - d  = D  + F  + A  + C

[Ear Training and Sight Singing: The Superpower you get from Music School - YouTube](https://www.youtube.com/watch?v=Ed2VUq9cu1I)
